/**
 * AppOperationsPage — the App Operations Board (ADR-075 step 5, D11).
 *
 * Per-app management surface in the vertical store for operators / tenant
 * admins (DoPlace operator for platform apps, OQ4b). Sections:
 *   1. Version history (deployment # + date primary, D8) with rollout status
 *      and the single-slot review queue made visible (D10)
 *   2. Development branches (dev-states from control-plane bookkeeping)
 *   3. App health + "Run verification"
 *   4. Change request / send-to-review triggers
 *   5. Access management (review + test roles; realm-wide note surfaced)
 *
 * Structurally absent here, by decision OQ1b: any approve / push-back control.
 * Approval stays a tenant-store action; this page renders decisions, never
 * makes them. Vocabulary is non-technical: the board persona sits between the
 * approver and the developer.
 *
 * Role gate (UX only — every endpoint re-checks server-side): tenant-admin /
 * broker-operator / service-admin / vertical-product-owner, from
 * auth/surfaceAccess. It does NOT mirror AdminPage, whatever this comment used
 * to claim: AdminPage admits core-developer / vertical-product-owner and no
 * tenant-admin. The board is deliberately the wider set — it is the tenant
 * admin's own surface (adr-075 D11).
 */

import { useState, type ReactNode } from 'react';
import { useAuth } from '../auth/KeycloakProvider';
import { APP_OPERATIONS_ROLES } from '../auth/surfaceAccess';
import { t } from '../i18n';
import {
  useDeployments, useBranchState, useAppHealth, useVerifyStatus,
  useAccessList, useBoardAction,
  type BoardDeployment,
} from '../hooks/useAppOperations';

const BOARD_UI_ROLES = APP_OPERATIONS_ROLES;

// ── Small presentational helpers ─────────────────────────────────────────────

function SectionCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--sp-6)',
        marginBottom: 'var(--sp-6)',
      }}
    >
      <h2 style={{ fontSize: 'var(--fs-h3)', margin: '0 0 var(--sp-4) 0' }}>{title}</h2>
      {children}
    </section>
  );
}

function StatusPill({ kind, label }: { kind: 'good' | 'warn' | 'bad' | 'muted'; label: string }) {
  const palette: Record<string, { bg: string; fg: string }> = {
    good:  { bg: 'var(--dp-green-100)',  fg: 'var(--dp-green-800)' },
    warn:  { bg: 'var(--dp-amber-100)',  fg: 'var(--dp-amber-800)' },
    bad:   { bg: 'var(--dp-crimson-100)', fg: 'var(--dp-crimson-800)' },
    muted: { bg: 'var(--dp-canvas-200)',           fg: 'var(--fg-3)' },
  };
  const c = palette[kind];
  return (
    <span style={{
      background: c.bg, color: c.fg, borderRadius: 'var(--radius-pill)',
      padding: '2px 10px', fontSize: 'var(--fs-meta)', fontFamily: 'var(--font-sans)',
      whiteSpace: 'nowrap',
    }}>{label}</span>
  );
}

function InlineNote({ children }: { children: ReactNode }) {
  return (
    <p style={{ color: 'var(--fg-3)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)', margin: 'var(--sp-2) 0 0 0' }}>
      {children}
    </p>
  );
}

/** Rollout status → non-technical label + pill kind (D8 vocabulary). */
function statusView(d: BoardDeployment): { label: string; kind: 'good' | 'warn' | 'bad' | 'muted' } {
  if (d.status === 'uat' && d.queued) return { label: t('opsBoard.deployments.status.queued'), kind: 'muted' };
  switch (d.status) {
    case 'uat':                   return { label: t('opsBoard.deployments.status.inReview'),  kind: 'warn' };
    case 'published':             return { label: t('opsBoard.deployments.status.live'),      kind: 'good' };
    case 'pushed_back':           return { label: t('opsBoard.deployments.status.sentBack'),  kind: 'bad' };
    case 'superseded_published':  return { label: t('opsBoard.deployments.status.replaced'),  kind: 'muted' };
    default:                      return { label: d.status, kind: 'muted' };
  }
}

function AccessDenied() {
  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 'var(--sp-3)', padding: 'var(--sp-12)' }}>
      <p className="dp-eyebrow">{t('opsBoard.denied.eyebrow')}</p>
      <h2>{t('opsBoard.denied.title')}</h2>
      <p style={{ color: 'var(--fg-3)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)' }}>
        {t('opsBoard.denied.body')}
      </p>
    </div>
  );
}

function LoadFailed({ what }: { what: string }) {
  return <InlineNote>{t('opsBoard.loadFailed').replace('{{what}}', what)}</InlineNote>;
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function AppOperationsPage({ appId }: { appId: string }) {
  const { user, token, hasRole } = useAuth();
  const [refreshKey, setRefreshKey] = useState(0);
  const bump = () => setRefreshKey((k) => k + 1);

  const deployments = useDeployments(appId, token, refreshKey);
  const branches = useBranchState(appId, token, refreshKey);
  const health = useAppHealth(appId, token, refreshKey);
  const verify = useVerifyStatus(appId, token, refreshKey);
  const access = useAccessList(appId, token, refreshKey);
  const { state: action, run } = useBoardAction(appId, token, bump);

  const [crComment, setCrComment] = useState('');
  const [crDeployNo, setCrDeployNo] = useState<number | null>(null);
  const [accessUser, setAccessUser] = useState('');

  const allowed = user && BOARD_UI_ROLES.some((r) => hasRole(r));
  if (!allowed) return <AccessDenied />;

  const busy = action.tag === 'submitting';

  // Change-requestable rows: sent-back or live (the change-loop entry points).
  const crCandidates = deployments.tag === 'loaded'
    ? deployments.data.deployments.filter((d) => d.status === 'pushed_back' || d.status === 'published')
    : [];
  const queueRows = deployments.tag === 'loaded'
    ? deployments.data.deployments.filter((d) => d.status === 'uat')
    : [];

  return (
    <div className="dp-shell__content" style={{ maxWidth: 960, margin: '0 auto', padding: 'var(--sp-6)' }}>
      <p className="dp-eyebrow">{t('opsBoard.eyebrow')}</p>
      <h1 style={{ margin: '0 0 var(--sp-2) 0' }}>{t('opsBoard.title').replace('{{app}}', appId)}</h1>
      <InlineNote>{t('opsBoard.intro')}</InlineNote>
      <div style={{ height: 'var(--sp-6)' }} />

      {/* Action feedback strip — declared results, never silent */}
      {action.tag === 'done' && (
        <div role="status" style={{ background: 'var(--dp-green-100)', borderRadius: 8, padding: 'var(--sp-3) var(--sp-4)', marginBottom: 'var(--sp-4)', fontSize: 'var(--fs-body-sm)' }}>
          {action.message ?? t('opsBoard.actionDone')}
        </div>
      )}
      {action.tag === 'error' && (
        <div role="alert" style={{ background: 'var(--dp-crimson-100)', borderRadius: 8, padding: 'var(--sp-3) var(--sp-4)', marginBottom: 'var(--sp-4)', fontSize: 'var(--fs-body-sm)' }}>
          {t('opsBoard.actionFailed').replace('{{reason}}', action.message)}
        </div>
      )}

      {/* ── 1. Version history + review queue (D8, D10) ────────────────────── */}
      <SectionCard title={t('opsBoard.deployments.title')}>
        {deployments.tag === 'loading' && <InlineNote>{t('opsBoard.loading')}</InlineNote>}
        {deployments.tag === 'error' && <LoadFailed what={t('opsBoard.deployments.title')} />}
        {deployments.tag === 'loaded' && (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--fg-3)' }}>
                  <th style={{ padding: 'var(--sp-2)' }}>{t('opsBoard.deployments.colVersion')}</th>
                  <th style={{ padding: 'var(--sp-2)' }}>{t('opsBoard.deployments.colStatus')}</th>
                  <th style={{ padding: 'var(--sp-2)' }}>{t('opsBoard.deployments.colDecision')}</th>
                </tr>
              </thead>
              <tbody>
                {deployments.data.deployments.map((d) => {
                  const sv = statusView(d);
                  return (
                    <tr key={d.deploy_no} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                      <td style={{ padding: 'var(--sp-2)' }}>
                        <strong>{t('opsBoard.deployments.versionLabel').replace('{{no}}', String(d.deploy_no)).replace('{{date}}', d.deploy_date)}</strong>
                        <div style={{ color: 'var(--fg-3)', fontSize: 'var(--fs-meta)' }}>{d.release_tag}</div>
                      </td>
                      <td style={{ padding: 'var(--sp-2)' }}><StatusPill kind={sv.kind} label={sv.label} /></td>
                      <td style={{ padding: 'var(--sp-2)', color: 'var(--fg-2)' }}>
                        {d.decided_by
                          ? <>
                              {t('opsBoard.deployments.decidedBy').replace('{{when}}', d.decided_at ? new Date(d.decided_at).toLocaleDateString() : '—')}
                              {d.decision_comment && <div style={{ color: 'var(--fg-3)', fontSize: 'var(--fs-meta)' }}>&ldquo;{d.decision_comment}&rdquo;</div>}
                            </>
                          : <span style={{ color: 'var(--fg-3)' }}>—</span>}
                      </td>
                    </tr>
                  );
                })}
                {deployments.data.deployments.length === 0 && (
                  <tr><td colSpan={3} style={{ padding: 'var(--sp-4)', color: 'var(--fg-3)' }}>{t('opsBoard.deployments.empty')}</td></tr>
                )}
              </tbody>
            </table>

            {/* Review queue (D10): the single slot + who is waiting */}
            <h3 style={{ fontSize: 'var(--fs-body)', margin: 'var(--sp-5) 0 var(--sp-2) 0' }}>{t('opsBoard.queue.title')}</h3>
            {queueRows.length === 0 && <InlineNote>{t('opsBoard.queue.empty')}</InlineNote>}
            {queueRows.length > 0 && (
              <ul style={{ margin: 0, paddingLeft: 'var(--sp-5)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)' }}>
                {queueRows.map((d) => (
                  <li key={d.deploy_no} style={{ marginBottom: 'var(--sp-1)' }}>
                    {t('opsBoard.deployments.versionLabel').replace('{{no}}', String(d.deploy_no)).replace('{{date}}', d.deploy_date)}
                    {' — '}
                    {d.queued ? t('opsBoard.queue.waiting') : t('opsBoard.queue.activeSlot')}
                  </li>
                ))}
              </ul>
            )}
            <InlineNote>{t('opsBoard.queue.note')}</InlineNote>
          </>
        )}
      </SectionCard>

      {/* ── 2. Development branches ────────────────────────────────────────── */}
      <SectionCard title={t('opsBoard.branches.title')}>
        {branches.tag === 'loading' && <InlineNote>{t('opsBoard.loading')}</InlineNote>}
        {branches.tag === 'error' && <LoadFailed what={t('opsBoard.branches.title')} />}
        {branches.tag === 'loaded' && (
          branches.data.branches.length === 0
            ? <InlineNote>{t('opsBoard.branches.empty')}</InlineNote>
            : (
              <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)' }}>
                {branches.data.branches.map((b) => (
                  <li key={b.branch} style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'center', padding: 'var(--sp-2) 0', borderTop: '1px solid var(--border-subtle)' }}>
                    <code>{b.branch}</code>
                    <StatusPill kind={b.open ? 'warn' : 'muted'} label={b.open ? t('opsBoard.branches.open') : t('opsBoard.branches.closed')} />
                  </li>
                ))}
              </ul>
            )
        )}
      </SectionCard>

      {/* ── 3. Health & verification (Part B) ─────────────────────────────── */}
      <SectionCard title={t('opsBoard.health.title')}>
        {health.tag === 'loading' && <InlineNote>{t('opsBoard.loading')}</InlineNote>}
        {health.tag === 'error' && <LoadFailed what={t('opsBoard.health.title')} />}
        {health.tag === 'loaded' && (
          <>
            <div style={{ marginBottom: 'var(--sp-3)' }}>
              <StatusPill
                kind={health.data.overall === 'healthy' ? 'good' : health.data.overall === 'degraded' ? 'warn' : 'bad'}
                label={t(`opsBoard.health.overall.${health.data.overall}` as Parameters<typeof t>[0])}
              />
            </div>
            <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)' }}>
              {health.data.checks.map((c) => (
                <li key={c.name} style={{ display: 'flex', gap: 'var(--sp-3)', padding: 'var(--sp-1) 0' }}>
                  <span style={{ minWidth: 120 }}>{c.name}</span>
                  <StatusPill kind={c.ok ? 'good' : 'bad'} label={c.ok ? t('opsBoard.health.ok') : t('opsBoard.health.failed')} />
                  {c.latency_ms !== null && <span style={{ color: 'var(--fg-3)' }}>{c.latency_ms} ms</span>}
                  {c.detail && <span style={{ color: 'var(--fg-3)' }}>{c.detail}</span>}
                </li>
              ))}
            </ul>
          </>
        )}
        <div style={{ marginTop: 'var(--sp-4)', display: 'flex', gap: 'var(--sp-3)', alignItems: 'center' }}>
          <button className="dp-btn" disabled={busy} onClick={() => void run('verify', '/verify')}>
            {busy && action.tag === 'submitting' && action.action === 'verify' ? t('opsBoard.working') : t('opsBoard.health.runVerification')}
          </button>
          {verify.tag === 'loaded' && verify.data.last && (
            <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)', color: 'var(--fg-2)' }}>
              {verify.data.last.result
                ? t('opsBoard.health.lastVerify')
                    .replace('{{pass}}', String(verify.data.last.result.pass))
                    .replace('{{fail}}', String(verify.data.last.result.fail))
                : t('opsBoard.health.verifyRunning')}
              {verify.data.last.result && verify.data.last.result.failed_gates.length > 0 &&
                ` (${verify.data.last.result.failed_gates.join(', ')})`}
            </span>
          )}
        </div>
      </SectionCard>

      {/* ── 4. Changes & review (Part C triggers) ─────────────────────────── */}
      <SectionCard title={t('opsBoard.changes.title')}>
        <InlineNote>{t('opsBoard.changes.intro')}</InlineNote>
        <div style={{ display: 'flex', gap: 'var(--sp-3)', marginTop: 'var(--sp-4)', flexWrap: 'wrap', alignItems: 'center' }}>
          <select
            value={crDeployNo ?? ''}
            onChange={(e) => setCrDeployNo(e.target.value ? Number(e.target.value) : null)}
            style={{ padding: 'var(--sp-2)', fontFamily: 'var(--font-sans)' }}
            aria-label={t('opsBoard.changes.pickVersion')}
          >
            <option value="">{t('opsBoard.changes.pickVersion')}</option>
            {crCandidates.map((d) => (
              <option key={d.deploy_no} value={d.deploy_no}>
                {t('opsBoard.deployments.versionLabel').replace('{{no}}', String(d.deploy_no)).replace('{{date}}', d.deploy_date)}
                {' — '}{statusView(d).label}
              </option>
            ))}
          </select>
          <input
            value={crComment}
            onChange={(e) => setCrComment(e.target.value)}
            placeholder={t('opsBoard.changes.commentPlaceholder')}
            style={{ flex: 1, minWidth: 220, padding: 'var(--sp-2)', fontFamily: 'var(--font-sans)' }}
          />
          <button
            className="dp-btn"
            disabled={busy || crDeployNo === null}
            onClick={() => void run('change-request', '/change-request', { body: { deploy_no: crDeployNo, ...(crComment ? { comment: crComment } : {}) } })}
          >
            {t('opsBoard.changes.openChangeRequest')}
          </button>
          <button
            className="dp-btn"
            disabled={busy}
            onClick={() => void run('send-to-uat', '/send-to-uat')}
          >
            {t('opsBoard.changes.sendToReview')}
          </button>
        </div>
        <InlineNote>{t('opsBoard.changes.reviewNote')}</InlineNote>
      </SectionCard>

      {/* ── 5. Access management (Part D) ─────────────────────────────────── */}
      <SectionCard title={t('opsBoard.access.title')}>
        <InlineNote>{t('opsBoard.access.realmNote')}</InlineNote>
        {access.tag === 'loading' && <InlineNote>{t('opsBoard.loading')}</InlineNote>}
        {access.tag === 'error' && <LoadFailed what={t('opsBoard.access.title')} />}
        {access.tag === 'loaded' && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)', marginTop: 'var(--sp-3)' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--fg-3)' }}>
                <th style={{ padding: 'var(--sp-2)' }}>{t('opsBoard.access.colUser')}</th>
                <th style={{ padding: 'var(--sp-2)' }}>{t('opsBoard.access.colApprover')}</th>
                <th style={{ padding: 'var(--sp-2)' }}>{t('opsBoard.access.colTester')}</th>
              </tr>
            </thead>
            <tbody>
              {access.data.users.map((u) => (
                <tr key={u.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                  <td style={{ padding: 'var(--sp-2)' }}>{u.username}<div style={{ color: 'var(--fg-3)', fontSize: 'var(--fs-meta)' }}>{u.email}</div></td>
                  {(['uat-approver', 'tester'] as const).map((role) => {
                    const has = role === 'uat-approver' ? u.uat_approver : u.tester;
                    return (
                      <td key={role} style={{ padding: 'var(--sp-2)' }}>
                        <button
                          className="dp-btn dp-btn--ghost"
                          disabled={busy}
                          onClick={() => void run(
                            `access-${has ? 'remove' : 'grant'}`,
                            `/access/${role}`,
                            { method: has ? 'DELETE' : 'POST', body: { username: u.username } },
                          )}
                        >
                          {has ? t('opsBoard.access.remove') : t('opsBoard.access.grant')}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div style={{ display: 'flex', gap: 'var(--sp-3)', marginTop: 'var(--sp-4)', alignItems: 'center' }}>
          <input
            value={accessUser}
            onChange={(e) => setAccessUser(e.target.value)}
            placeholder={t('opsBoard.access.addPlaceholder')}
            style={{ minWidth: 220, padding: 'var(--sp-2)', fontFamily: 'var(--font-sans)' }}
          />
          <button
            className="dp-btn"
            disabled={busy || accessUser.trim().length === 0}
            onClick={() => void run('access-grant', '/access/uat-approver', { body: { username: accessUser.trim() } })}
          >
            {t('opsBoard.access.addApprover')}
          </button>
          <button
            className="dp-btn"
            disabled={busy || accessUser.trim().length === 0}
            onClick={() => void run('access-grant', '/access/tester', { body: { username: accessUser.trim() } })}
          >
            {t('opsBoard.access.addTester')}
          </button>
        </div>
      </SectionCard>
    </div>
  );
}
